import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICompanyProviders } from 'store/companies/companiesTypes';
import {
  DataType,
  FilterType,
  IProvider,
  IProviderItemLinkView,
  IProviderItemView,
  IProviderItemViewWithData,
  IProviders,
  ITagView,
  ITEM_MIN_ORDER,
  ItemSearchField,
  ProviderItemType,
  providerItemType,
  SurveyOwnerProperties,
} from './providerTypes';
import {
  IChoiceView,
  IMultipleChoiceQuestionView,
  IQuestionsView,
  ISurveyResponseView,
  ISurveyView,
  QuestionType,
} from '../survey/surveyTypes';
import { RootState, TThunkApi } from '../store';
import * as DocumentExports from '../entity/Document';
import Document, { documentFormat } from '../entity/Document';
import { v1 as uuid } from 'uuid';
import ProviderDocument from './providerUtils';
import { isANumber, randomNumber } from 'utils/number';
import { loadStatus } from '../entity/types';
import * as manApi from 'api/ManApi';
import $ from 'jquery';
import * as FileUtils from 'utils/file';
import { replaceAllApartLinks } from 'utils/string';
import { LimitationType } from '../limitations/limitationsSlice';
import {
  searchInFolderStarted,
  setSearchItems as setAppSearchItems,
} from '../app/appSlice';
import { addCompanyProviders } from '../companies/companiesSlice';
import { updateNestedObject } from '../objectHelpers';
import { produce } from 'immer';
import UserMixpanel from '../../utils/mixpanel/MixpanelClass';

export enum itemStatus {
  creating = 'creating',
  exists = 'exists',
  uploading = 'uploading',
}

const initialState: IProviders = {
  items: {
    /*
       [provider_id]=>{
       'search': {'search_term': item_id[]}
       'items'=>[item_id]=>{'data'=>providerItem,'isLoading'=t|f},
       'folders'=>[item_id]=>{
       'isLoading'=>t|f
       'progress'=>0~100
       }
       }
       */
  },
  tags: {
    /*
       [provider_id]=>{'data','status'=t|f'}
       */
  },
  usernameMap: {},
  map: {
    /*
       (provider_id) => {'data','isValid','isLoading'}
       */
  },
};

// Helper Functions
function updateSurveyOwnerPropertiesHelper(
  state: IProviders,
  action: PayloadAction<{
    providerId: number;
    surveyOwnerProperties: SurveyOwnerProperties;
  }>
) {
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `map[${action.payload.providerId}].surveyOwnerProperties`,
    value: action.payload.surveyOwnerProperties,
  });
}

// Helper Functions
function updateProviderDataHelper(
  state: IProviders,
  action: PayloadAction<{ provider: IProvider }>
) {
  const provider = action.payload.provider;

  state = updateNestedObject<IProviders>({
    obj: state,
    path: `map[${provider.id}]`,
    value: { data: provider, isValid: true, isLoading: false },
  });

  if (provider.username) {
    state = updateNestedObject<IProviders>({
      obj: state,
      path: `usernameMap[${provider.username}]`,
      value: provider.id,
    });
  }

  let previousFolder = state.items[provider.id]?.folders ?? {};

  if (
    provider.rootDocument &&
    previousFolder?.[provider.rootDocument.id]?.state !== 'loaded'
  ) {
    previousFolder = updateNestedObject<{ [key: number]: { state: string } }>({
      obj: previousFolder,
      path: `[${provider.rootDocument.id}].state`,
      value: 'notLoaded',
    });
  }

  let previousItems = state.items[provider.id]?.items ?? {};

  // if (provider.rootDocument) {
  //   previousItems = updateNestedObject<{
  //     [key: number]: IProviderItemViewWithData;
  //   }>({
  //     obj: previousItems,
  //     path: '[provider.rootDocument.id]',
  //     value: {
  //       data: provider.rootDocument,
  //       state: 'loaded',
  //     },
  //   });
  // }

  [provider.rootDocument, ...provider.documents].forEach((value) => {
    previousItems = updateNestedObject<{
      [key: number]: IProviderItemViewWithData;
    }>({
      obj: previousItems,
      path: `[${value!.id!}]`,
      value: {
        data: value,
        state: 'loaded',
      },
    });
  });

  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items.[${provider.id}]`,
    value: {
      folders: previousFolder,
      items: previousItems,
      search: {},
    },
  });
}

function computeQuestionDisplayToValueHelper(survey: ISurveyView) {
  const questions = survey.questionMap ?? {};

  return Object.keys(questions)?.reduce(
    (all: { [key: string]: string }, current: string, questionIndex) => {
      let text = (questions[current].data as IQuestionsView).title;
      if (all[text] !== undefined) {
        text = text + ' [' + questionIndex + ']';
      }
      all[text] = current;
      return all;
    },
    {}
  );
}

function computeAnswerDisplayToValueHelper(survey: ISurveyView) {
  const questions = survey.questionMap ?? {};

  return Object.keys(questions)?.reduce(
    (all: { [key: string]: string }, current: string, questionIndex) => {
      if (questions[current].data.type !== QuestionType.MultipleChoice) {
        return all;
      }
      (
        (questions[current].data as IMultipleChoiceQuestionView).choiceList ||
        []
      ).forEach((choice: IChoiceView, choiceIndex: number) => {
        let text = choice.text;
        if (all[choice.text] !== undefined) {
          const questionTitle = questions[current].data.title;
          let found = false;
          for (
            let subStringLength = 10;
            subStringLength <= questionTitle.length;
            subStringLength += 10
          ) {
            text =
              choice.text +
              ' ( ' +
              questionTitle.substring(0, subStringLength) +
              ' ...)';
            if (all[text] === undefined) {
              found = true;
              break;
            }
          }
          if (!found) {
            text =
              choice.text +
              ' [' +
              (questionIndex + 1) +
              '][' +
              (choiceIndex + 1) +
              ']';
          }
        }
        all[text] = choice.value;
      });
      return all;
    },
    {}
  );
}

function checkFiltersHelper(chart: any, questionDisplayToValue: any) {
  if (chart.filters) {
    chart.filters.map((filter: any) => {
      if (
        filter.type === FilterType.term &&
        filter.dataType === DataType.question
      ) {
        filter.displayToValue = questionDisplayToValue;
      }
      return filter;
    });
  }
  return chart;
}

function checkBucketsHelper(
  bucket: any,
  questionDisplayToValue: any,
  answerDisplayToValue: any
) {
  if (bucket.dataType === DataType.question) {
    bucket.displayToValue = questionDisplayToValue;
  }
  if (bucket.dataType === DataType.answer) {
    bucket.displayToValue = answerDisplayToValue;
  }
  if (bucket.subBuckets) {
    bucket.subBuckets.forEach((subBucket: any) =>
      checkBucketsHelper(
        subBucket,
        questionDisplayToValue,
        answerDisplayToValue
      )
    );
  }
  return bucket;
}

function updateItemHelper(
  remoteItem: IProviderItemView,
  old?: IProviderItemView | null,
  dataRoot?: any
) {
  const newItem = remoteItem;

  if (!old) {
    return newItem;
  } else if (newItem.type === providerItemType.survey) {
    const surveyData =
      dataRoot && newItem.surveyId
        ? dataRoot?.survey?.data[newItem.surveyId]?.data
        : undefined;

    return surveyData === undefined
      ? newItem
      : fillChartFromSurveyHelper(newItem, surveyData);
  }

  return newItem;
}

export function fillChartFromSurveyHelper(item: any, survey: any) {
  const chart = item.chart || [];

  const questionDisplayToValue = computeQuestionDisplayToValueHelper(survey);
  const answerDisplayToValue = computeAnswerDisplayToValueHelper(survey);
  checkFiltersHelper(chart, questionDisplayToValue);

  if (chart.buckets) {
    chart.buckets.forEach((bucket: any) =>
      checkBucketsHelper(bucket, questionDisplayToValue, answerDisplayToValue)
    );
  }

  const result = Object.assign({}, item);
  result.chart = chart;
  return result;
}

// Reducers
function survey_addResponseHandler(
  state: IProviders,
  action: PayloadAction<{
    response: ISurveyResponseView;
    surveyId: number;
    providerId: number;
    itemId: number;
    effectiveDateString: string;
  }>
) {
  // produce(state, (draft) => {
  state.items[action.payload.providerId].items[
    action.payload.itemId
  ].data.myResponseInfo = {
    filledPeriods: [action.payload.effectiveDateString || 'filled'],
  };
  // });
}

function loadingProviderDataHandler(
  state: IProviders,
  action: PayloadAction<{ providerId: number }>
) {
  if (state.map[action.payload.providerId]) {
    state.map[action.payload.providerId] = {
      ...state.map[action.payload.providerId],
      isLoading: true,
    };
  } else {
    // @ts-expect-error updating step by step
    state.map = {
      ...state.map,
      [action.payload.providerId]: {
        isLoading: true,
      },
    };
  }
}

function addProviderDataHandler(
  state: IProviders,
  action: PayloadAction<{ provider: IProvider }>
) {
  updateProviderDataHelper(state, action);
}

function registerProviderDataHandler(
  state: IProviders,
  action: PayloadAction<{ provider: IProvider }>
) {
  updateProviderDataHelper(state, action);
}

function updateProviderDataHandler(
  state: IProviders,
  action: PayloadAction<{ provider: IProvider }>
) {
  updateProviderDataHelper(state, action);
}

function updateSurveyOwnerPropertiesHandler(
  state: IProviders,
  action: PayloadAction<{
    providerId: number;
    surveyOwnerProperties: SurveyOwnerProperties;
  }>
) {
  updateSurveyOwnerPropertiesHelper(state, action);
}

function addProviderDataFailedHandler(
  state: IProviders,
  action: PayloadAction<{
    providerId: number | null;
    username: string | null;
    err: any;
  }>
) {
  if (action.payload.providerId) {
    if (action.payload.err.code === 400) {
      state.map[action.payload.providerId] = {
        data: null,
        isValid: false,
        isLoading: false,
      };
    } else {
      state.map[action.payload.providerId] = {
        data: null,
        isValid: false,
        isLoading: false,
      };
    }
  }
}

function thisUser_setProviderStatusHandler(
  state: IProviders,
  action: PayloadAction<{ provider: IProvider }>
) {
  if (action.payload.provider.status.register === 'Registered') {
    updateProviderDataHelper(state, action);
  }
}

function addAllItemHandler(
  state: IProviders,
  action: PayloadAction<{
    items: Array<IProviderItemView>;
    providerId: number;
    folderId?: number;
  }>
) {
  const folderItem = action.payload.folderId
    ? state.items[action.payload.providerId]?.items?.[action.payload.folderId]
        ?.data
    : undefined;

  const hasFolderItem = Boolean(folderItem);
  const virtualChildren: number[] = [];
  action.payload.items?.forEach((itm) => {
    if (itm === null) return;
    const oldItm = state.items[itm.providerId]?.items?.[itm.id]?.data ?? null;

    state = updateNestedObject<IProviders>({
      obj: state,
      path: `items[${itm.providerId}].items[${itm.id}].data`,
      value: {
        ...(state.items[itm.providerId]?.items?.[itm.id]?.data ?? {}),
        ...updateItemHelper(itm, oldItm),
      },
    });

    state.items[itm.providerId].items[itm.id].state = 'loaded';

    if (hasFolderItem && folderItem?.type === 'searchTag') {
      virtualChildren.push(itm.id);
    }
  });

  if (hasFolderItem && folderItem?.type === 'searchTag') {
    state = updateNestedObject<IProviders>({
      obj: state,
      path: `items[${action.payload.providerId}].items[${action.payload.folderId}].data.virtualChildren`,
      value: virtualChildren,
    });
  }

  if (action.payload.folderId) {
    state = updateNestedObject<IProviders>({
      obj: state,
      path: `items[${action.payload.providerId}].folders[${action.payload.folderId}].state`,
      value: 'loaded',
    });
  }
}

function loadingDocumentsInFolderHandler(
  state: IProviders,
  action: PayloadAction<{
    providerId: number;
    folderId: number;
  }>
) {
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items[${action.payload.providerId}].folders[${action.payload.folderId}].state`,
    value: 'loading',
  });
}

function failToGetDocumentsInFolderHandler(
  state: IProviders,
  action: PayloadAction<{
    providerId: number;
    folderId: number;
  }>
) {
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items[${action.payload.providerId}].folders[${action.payload.folderId}].state`,
    value: 'failed',
  });
}

function creatingNewItemHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemView; error: unknown }>
) {
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items[${action.payload.item.providerId}].items[${action.payload.item.id}]`,
    value: {
      data: action.payload.item,
      state: 'loaded',
      status: itemStatus.creating,
      error: action.payload.error,
    },
  });
}

function createNewItemHandler(
  state: IProviders,
  action: PayloadAction<{
    providerId: number;
    item: IProviderItemView;
    type: ProviderItemType;
  }>
) {
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items[${action.payload.item.providerId}].items[${action.payload.item.id}]`,
    value: {
      data: action.payload.item,
      state: 'loaded',
      status: itemStatus.creating,
    },
  });
}

function failToUploadItemHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemView; err: any }>
) {
  const { item, err } = action.payload;
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items[${item.providerId}].items[${action.payload.item.id}]`,
    value: {
      data: item,
      isLoading: false,
      status: itemStatus.creating,
      error: err,
    },
  });
}

function creatingItemCanceledHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemView }>
) {
  delete state.items[action.payload.item.providerId]?.items?.[
    action.payload.item.id
  ];
}

function uploadingNewItemHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemViewWithData }>
) {
  produce(state, () => {
    state.items[action.payload.item.data.providerId].items[
      action.payload.item.data.id
    ] = {
      data: action.payload.item.data,
      isLoading: true,
      status: itemStatus.uploading,
    };
  });
}

function updateItemProgressHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemView; progress: number }>
) {
  const { item, progress } = action.payload;

  produce(state, (state) => {
    if (state.items[item.providerId].items[item.id]) {
      state.items[item.providerId].items[item.id] = {
        ...state.items[item.providerId].items[item.id],
        progress,
      };
    }
  });
}

function updateLocalItemHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemView }>
) {
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items[${action.payload.item.providerId}].items[${action.payload.item.id}].data`,
    value: updateItemHelper(
      action.payload.item,
      state.items[action.payload.item.providerId].items[action.payload.item.id]
        .data
    ),
  });
}

function itemCreatedHandler(
  state: IProviders,
  action: PayloadAction<{
    localItem: IProviderItemView;
    remoteItem: IProviderItemView;
    dataRoot?: any;
  }>
) {
  const old =
    state.items[action.payload.remoteItem.providerId]?.items[
      action.payload.remoteItem.id
    ]?.data ?? null;

  state.items[action.payload.remoteItem.providerId].items[
    action.payload.remoteItem.id
  ] = {
    data: {
      ...updateItemHelper(
        action.payload.remoteItem,
        old,
        action?.payload?.dataRoot
      ),
    },
    isLoading: false,
    status: itemStatus.exists,
  };

  if (
    action.payload.localItem.id !== null &&
    action.payload.localItem.id !== action.payload.remoteItem.id
  ) {
    delete state.items[action.payload.localItem.providerId].items[
      action.payload.localItem.id
    ];
  }
  if (action.payload.remoteItem.dadId === null) {
    state.map[action.payload.remoteItem.providerId].data?.documents?.push(
      action.payload.remoteItem
    );
  }
}

function deleteItemHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemView }>
) {
  if (typeof action.payload?.item?.dadId === 'number') {
    delete state.items?.[action.payload.item.providerId]?.items?.[
      action.payload?.item?.id
    ];
  } else {
    const documents = state?.map[
      action.payload?.item?.providerId
    ]?.data?.documents?.filter((it) => action.payload?.item?.id !== it?.id);

    if (state?.map[action.payload?.item?.providerId]?.data?.documents) {
      produce(state, () => {
        if (
          state.map[action.payload.item.providerId].data?.documents &&
          documents
        ) {
          state.map[action.payload.item.providerId].data!.documents = documents;
        }
      });
    }
  }
}

function setTagsStatusHandler(
  state: IProviders,
  action: PayloadAction<{ providerId: number; status: loadStatus }>
) {
  state = updateNestedObject<IProviders>({
    obj: state,
    path: `tags[${action.payload.providerId}].status`,
    value: action.payload.status,
  });
}

function addTagsHandler(
  state: IProviders,
  action: PayloadAction<{ providerId: number; tags: any }>
) {
  state.tags[action.payload.providerId]!.status = 'loaded';
  action.payload.tags.forEach((tag: ITagView) => {
    state = updateNestedObject<IProviders>({
      obj: state,
      path: `tags[${action.payload.providerId}].data[${tag.id}]`,
      value: tag,
    });
  });
}

function createNewTagHandler(
  state: IProviders,
  action: PayloadAction<{ providerId: number; parentId: number }>
) {
  const tag = {
    parentId: action.payload.parentId,
    id: uuid(),
    text: '',
    status: 'creating',
  };

  if (!state.tags[action.payload.providerId]) {
    // @ts-expect-error updating step by step
    state.tags = {
      ...state.tags,
      [action.payload.providerId]: { data: {} },
    };
  }

  state = updateNestedObject<IProviders>({
    obj: state,
    path: `tags[${action.payload.providerId}].data[${tag.id}]`,
    value: {
      ...tag,
      status: itemStatus.creating,
    },
  });

  state = updateNestedObject<IProviders>({
    obj: state,
    path: `tags[${action.payload.providerId}].status`,
    value: itemStatus.creating,
  });
}

function removeTagHandler(
  state: IProviders,
  action: PayloadAction<{ providerId: number; tagId: number | string }>
) {
  delete state.tags[action.payload.providerId]?.data[action.payload.tagId];
}

function updateTagHandler(
  state: IProviders,
  action: PayloadAction<{
    providerId: number;
    tag?: ITagView;
    tags?: ITagView[];
    info?: string;
  }>
) {
  if (action.payload.tags) {
    action.payload.tags.forEach((tag: ITagView) => {
      state.tags[action.payload.providerId]!.data[tag.id] = tag;
    });
  }

  if (action.payload.tag) {
    state.tags[action.payload.providerId]!.data[action.payload.tag.id] =
      action.payload.tag;
  }
}

function addSingleItemHandler(
  state: IProviders,
  action: PayloadAction<{ item: IProviderItemView }>
) {
  const old =
    state.items[action.payload.item.providerId]?.items[action.payload.item.id]
      ?.data;

  state = updateNestedObject({
    obj: state,
    path: `items[${action.payload.item.providerId}].items[${action.payload.item.id}].data`,
    value: updateItemHelper(action.payload.item, old),
  });
}

function addSeveralProvidersHandler(
  state: IProviders,
  action: PayloadAction<{ providers: Array<IProvider> }>
) {
  action.payload.providers.forEach((provider: IProvider) => {
    updateProviderDataHelper(state, {
      payload: { provider },
      type: action.type,
    });
  });

  state.myProvidersStatus = 'loaded';
}

function setMyProvidersLoadingHandler(state: IProviders) {
  state.myProvidersStatus = 'loading';
}

function setSearchItemsHandler(
  state: IProviders,
  action: PayloadAction<{
    searchTerm: string;
    providerId: number;
    items: IProviderItemView[];
  }>
) {
  produce(state, () => {
    state.items[action.payload.providerId].search[action.payload.searchTerm] =
      action.payload.items.map((itm) => itm.id);

    action.payload.items.forEach((itm) => {
      const oldValue = state.items[itm.providerId]?.items[itm.id]?.data;

      state.items[itm.providerId].items[itm.id] = {
        data: updateItemHelper(itm, oldValue),
        state: 'loaded',
      };
    });
  });
}

function itemLinkCreatedHandler(
  state: IProviders,
  action: PayloadAction<{
    sourceItemId: number;
    itemLink: IProviderItemLinkView;
    tempItemId: number | null;
    providerId: number;
  }>
) {
  if (action.payload.tempItemId) {
    delete state.items[action.payload.providerId].items[
      action.payload.tempItemId
    ];
  }

  if (
    !state.items[action.payload.providerId]?.items?.[
      action.payload.sourceItemId
    ]?.data?.itemLinks
  ) {
    state = updateNestedObject({
      obj: state,
      path: `items[${action.payload.providerId}].items[${action.payload.sourceItemId}].data.itemLinks`,
      value: [],
    });
  }

  state.items[action.payload.providerId].items[
    action.payload.sourceItemId
  ].data.itemLinks.push(action.payload.itemLink);
}

function itemLinkLoadingHandler(
  state: IProviders,
  action: PayloadAction<{ itemId: number; providerId: number }>
) {
  const old =
    state.items[action.payload.providerId].items[action.payload.itemId].data;

  state = updateNestedObject<IProviders>({
    obj: state,
    path: `items[${action.payload.providerId}].items[${action.payload.itemId}]`,
    value: {
      data: old,
      isLoading: true,
      status: itemStatus.uploading,
    },
  });
}

function deleteItemLinkHandler(
  state: IProviders,
  action: PayloadAction<{
    linkId: number;
    sourceItemId: number;
    providerId: number;
  }>
) {
  const links =
    state.items[action.payload.providerId]?.items?.[action.payload.sourceItemId]
      ?.data?.itemLinks ?? [];
  state.items[action.payload.providerId].items[
    action.payload.sourceItemId
  ].data.itemLinks = links.filter((link) => link.id !== action.payload.linkId);
  // state = updateNestedObject({
  //   obj: state,
  //   path: `items[${action.payload.providerId}].items[${action.payload.sourceItemId}].data.itemLinks`,
  //   value: links.filter((link) => link.id !== action.payload.linkId),
  // });
}

// Thunks
export const creatingNewProviderItemFn = async (
  {
    providerId,
    dadId,
    dataUrl,
    fileSize,
    displayName,
    format,
    photo,
    order = 0,
    originalFileName,
    error,
  }: {
    providerId: number;
    dadId: number;
    dataUrl: string;
    fileSize: any;
    displayName: string;
    format: any;
    photo: any;
    order: number;
    originalFileName: any;
    error: any;
  },
  ThunkApi: TThunkApi
) => {
  const documentProperties = new DocumentExports.DocumentProperties(
    format,
    displayName,
    photo,
    null,
    originalFileName
  );
  const document = new Document(
    uuid(),
    DocumentExports.documentType.info,
    documentProperties,
    dataUrl,
    fileSize
  );
  const item = new ProviderDocument(
    providerId,
    'exists',
    dadId,
    uuid(),
    document,
    true,
    order
  );

  ThunkApi.dispatch(
    creatingNewItem({ item: item as IProviderItemView, error })
  );
};
const creatingNewProviderItem = createAsyncThunk(
  'providers/creatingNewProviderItem',
  creatingNewProviderItemFn
);

export const createNewProviderItemFn = async (
  {
    providerId,
    dadId = null,
    type,
    tags,
  }: {
    providerId: number;
    dadId?: number | null;
    type: ProviderItemType;
    tags?: ITagView[];
  },
  ThunkApi: TThunkApi
) => {
  const item = {
    providerId,
    status: 'exists',
    dadId,
    id: randomNumber(10),
    name: '',
    type,
    order: ITEM_MIN_ORDER,
    editable: true,
    tags,
  } as IProviderItemView;

  if (type === 'document') {
    item.document = new Document(
      null,
      DocumentExports.documentType.info,
      { format: DocumentExports.documentFormat.quill } as any,
      null,
      null
    );
  }

  ThunkApi.dispatch(
    createNewItem({
      providerId,
      item,
      type,
    })
  );

  return Promise.resolve();
};
const createNewProviderItem = createAsyncThunk(
  'providers/createNewProviderItem',
  createNewProviderItemFn
);

export const requireProviderFn = async (
  { providerId }: { providerId: string | number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  ThunkApi.dispatch(
    loadingProviderData({
      providerId: Number(providerId),
    })
  );

  if (typeof providerId === 'string') {
    const id = store.providers.usernameMap[providerId];
    if (isANumber(id)) {
      providerId = id;
    } else {
      return manApi
        .provider_get(store, null, providerId)
        .then((res) => {
          ThunkApi.dispatch(addProviderData({ provider: res.data }));
          return Promise.resolve();
        })
        .catch((err) => {
          ThunkApi.dispatch(
            addProviderDataFailed({
              username: null,
              providerId: Number(providerId),
              err: err,
            })
          );
          return ThunkApi.rejectWithValue(err) as any;
        });
    }
  }

  const providerIsValid =
    store.providers?.map[providerId]?.isValid ??
    store.providers?.map[providerId]?.isLoading ??
    false;

  if (providerIsValid) {
    return Promise.resolve();
  }
  return manApi.provider_get(store, providerId, null).then(
    (res) => {
      ThunkApi.dispatch(
        addProviderData({
          provider: res.data,
        })
      );
      return Promise.resolve();
    },
    (err) => {
      ThunkApi.dispatch(
        addProviderDataFailed({
          providerId: Number(providerId),
          username: null,
          err: err,
        })
      );
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const requireProvider = createAsyncThunk(
  'providers/requireProvider',
  requireProviderFn
);

export const registerProviderFn = async (
  { provider }: { provider: IProvider },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .provider_register(provider, store)
    .then((res) => {
      ThunkApi.dispatch(registerProviderData({ provider: res.data }));
      return Promise.resolve({ provider: res.data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const registerProvider = createAsyncThunk(
  'providers/registerProvider',
  registerProviderFn
);

export const importContentFromRemoteFn = async (
  {
    sourceItemId,
    targetItemId,
    remoteUrl,
    readAccessToken,
    forceCopy,
    excludeSrcFolder,
    providerId,
  }: {
    sourceItemId: number;
    targetItemId: number;
    remoteUrl: string;
    readAccessToken: string;
    forceCopy: boolean;
    excludeSrcFolder: boolean;
    providerId: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .provider_importContentFromRemote(
      sourceItemId,
      targetItemId,
      remoteUrl,
      readAccessToken,
      forceCopy,
      excludeSrcFolder,
      store
    )
    .then((_) => {
      ThunkApi.dispatch(
        requireDocumentInFolder({
          folderId: targetItemId,
          providerId: providerId,
          forceLoad: true,
        })
      );
      return Promise.resolve({
        folderId: targetItemId,
        providerId: providerId,
        forceLoad: true,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const importContentFromRemote = createAsyncThunk(
  'providers/importContentFromRemote',
  importContentFromRemoteFn
);

export const updateProviderFn = async (
  { provider }: { provider: IProvider },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .provider_update(provider, store)
    .then((res) => {
      ThunkApi.dispatch(
        updateProviderData({
          provider: res.data,
        })
      );
      return Promise.resolve({
        provider: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateProvider = createAsyncThunk(
  'providers/updateProvider',
  updateProviderFn
);

export const updateSurveyOwnerPropertiesFn = async (
  {
    surveyOwnerProperties,
    token,
    providerId,
  }: {
    surveyOwnerProperties: SurveyOwnerProperties;
    token: string;
    providerId: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .surveyOwnerProperties_update(surveyOwnerProperties, token, store)
    .then((res) => {
      ThunkApi.dispatch(
        updateSurveyOwnerPropertiesData({
          surveyOwnerProperties: res.data,
          providerId,
        })
      );
      return Promise.resolve({
        surveyOwnerProperties: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};

const updateSurveyOwnerProperties = createAsyncThunk(
  'providers/updateSurveyOwnerProperties',
  updateSurveyOwnerPropertiesFn
);

export const requireDocumentInFolderFn = async (
  {
    folderId,
    providerId,
    forceLoad = false,
    includeDeleted = false,
  }: {
    folderId: number;
    providerId: number;
    forceLoad?: boolean;
    includeDeleted?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const folderState =
    store.providers.items[providerId]?.folders?.[folderId] ?? undefined;

  if (!folderState || folderState.state !== 'loaded' || forceLoad) {
    if (!forceLoad) {
      ThunkApi.dispatch(loadingDocumentsInFolder({ providerId, folderId }));
    }
    return await manApi
      .provider_getDocumentsInFolder(folderId, store, 0, 200, includeDeleted)
      .then((res) => {
        ThunkApi.dispatch(
          addAllItem({
            items: res.data,
            providerId,
            folderId,
          })
        );
        return Promise.resolve({
          items: res.data,
        });
      })
      .catch((err) => {
        ThunkApi.dispatch(
          failToGetDocumentsInFolder({
            providerId,
            folderId,
          })
        );
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  } else {
    const allItems = store.providers.items[Number(providerId)].items;
    const virtualChildren = allItems[folderId]?.data?.virtualChildren;
    const items = Object.values(allItems).reduce(
      (ret: IProviderItemView[], item) => {
        if (
          item.data.dadId === folderId ||
          item.data.referenced?.find(
            (link: any) => link.sourceItemId === folderId
          ) ||
          (virtualChildren &&
            Array.isArray(virtualChildren) &&
            virtualChildren.includes(item.data.id))
        )
          ret.push(item.data);
        return ret;
      },
      [] as IProviderItemView[]
    );
    return Promise.resolve({
      items: items,
    });
  }
};

const requireDocumentInFolder = createAsyncThunk(
  'providers/requireDocumentInFolder',
  requireDocumentInFolderFn
);

export const requireSearchTagFn = async (
  {
    searchTagId,
    start,
    count,
  }: { searchTagId: number; start: number; count: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .provider_getDocumentsInFolder(searchTagId, store, start, count)
    .then((res) => {
      return Promise.resolve(res);
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const requireSearchTag = createAsyncThunk(
  'providers/requireSearchTag',
  requireSearchTagFn
);

export const item_createNewFolderFn = async (
  {
    item,
  }: {
    item: IProviderItemView;
  },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    createItem({
      item: item,
      apiPromise: (itemCopy, state) => {
        itemCopy.id = undefined as any;
        return manApi.provider_addFolder(itemCopy, state);
      },
    })
  );
};
const item_createNewFolder = createAsyncThunk(
  'providers/item_createNewFolder',
  item_createNewFolderFn
);

export const item_createNewDocumentFn = async (
  {
    item,
    file,
    setProgress,
  }: {
    item: IProviderItemView;
    file: any;
    setProgress?: (p: number) => void;
  },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    createItem({
      item: item,
      apiPromise: (toUploadItem, state) => {
        const fileName =
          toUploadItem.document.properties.originalFileName || 'fileName';
        toUploadItem.document.id = null;
        toUploadItem.document.url = null;
        toUploadItem.id = undefined as any;
        toUploadItem.document.properties.displayName = fileName;
        delete toUploadItem.document.properties.originalFileName;
        return manApi.provider_addDocument(
          toUploadItem,
          file,
          fileName,
          state,
          (progress: any) => {
            updateItemProgressHandler(state.providers, {
              payload: {
                item,
                progress,
              },
              type: '',
            });
            if (setProgress) {
              setProgress(progress);
            }
          }
        );
      },
    })
  );
};
const item_createNewDocument = createAsyncThunk(
  'providers/item_createNewDocument',
  item_createNewDocumentFn
);

export const item_createNewDocumentVersionFn = async (
  {
    providerItemId,
    file,
    filename,
    versionName,
    versionMessage,
    progressCallBack,
  }: {
    providerItemId: number;
    file: any;
    filename: string;
    versionName: string;
    versionMessage: string | null;
    progressCallBack: any;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .provider_addDocumentVersion(
      providerItemId,
      file,
      filename,
      versionName,
      versionMessage,
      store,
      progressCallBack
    )
    .then((res) => {
      ThunkApi.dispatch(addSingleItem({ item: res.data }));
      return Promise.resolve({
        item: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      //return Promise.reject(err);
    });
};
const item_createNewDocumentVersion = createAsyncThunk(
  'providers/item_createNewDocumentVersion',
  item_createNewDocumentVersionFn
);

export const item_createNewQuillFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  // console.log(item);
  const store = ThunkApi.getState() as RootState;
  const reduxItem = store.providers.items[item.providerId].items[item.id];
  if (reduxItem === undefined || reduxItem.status === itemStatus.creating) {
    const toUploadItem = JSON.parse(JSON.stringify(item));
    const mx = UserMixpanel.getInstance();
    toUploadItem.id = null;

    ThunkApi.dispatch(uploadingNewItem({ item: { data: { ...item } } }));

    return manApi
      .provider_addQuill(toUploadItem, store)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({
            localItem: item,
            remoteItem: res.data,
            dataRoot: undefined,
          })
        );
        mx.sendCreateEditPageTrack(`${toUploadItem.id} - ${toUploadItem.name}`);
        return Promise.resolve();
      })
      .catch((err) => {
        ThunkApi.dispatch(
          failToUploadItem({
            item,
            err,
          })
        );
        ThunkApi.rejectWithValue(err) as any;
        return Promise.reject({ item, err });
      });
  }
  return;
};
const item_createNewQuill = createAsyncThunk(
  'providers/item_createNewQuill',
  item_createNewQuillFn
);

export const item_deleteFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .provider_deleteDocument(item?.id, store)
    .then(() => {
      ThunkApi.dispatch(deleteItem({ item }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const item_delete = createAsyncThunk('providers/item_delete', item_deleteFn);

export const quill_updateFn = async (
  {
    deltas,
    htmlNode,
    item,
    progressCallBack,
  }: {
    deltas: any;
    htmlNode: any;
    item: IProviderItemView;
    progressCallBack: any;
  },
  ThunkApi: TThunkApi
) => {
  // console.log(item);
  const mx = UserMixpanel.getInstance();

  const store = ThunkApi.getState() as RootState;
  const images = $(htmlNode).find('img').toArray();
  const fileNameToBlob = {};
  const fileNameToDataUrl = {};
  for (const image of images) {
    const src = $(image).attr('src');
    const fileName = uuid();
    if (FileUtils.isDataURL(src)) {
      fileNameToDataUrl[fileName] = src;
      const blob = FileUtils.dataURItoBlob(src);
      const blobUrl = URL.createObjectURL(blob);
      $(image).attr('src', blobUrl);
      $(image).attr('data-fileName', fileName);
      fileNameToBlob[fileName] = blob;
    }
  }
  const htmlString = replaceAllApartLinks(htmlNode.innerHTML);

  ThunkApi.dispatch(uploadingNewItem({ item: { data: { ...item } } }));

  return manApi
    .provider_updateQuill(
      item.id,
      item,
      deltas,
      htmlString,
      fileNameToBlob,
      store,
      progressCallBack
    )
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      mx.sendCreateEditPageTrack(`${item.id} - ${item.name}`);
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const quill_update = createAsyncThunk('providers/quill_update', quill_updateFn);

export const update_ckFn = async (
  {
    content,
    item,
    progressCallBack,
  }: {
    content: string;
    item: IProviderItemViewWithData;
    progressCallBack: any;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const fileNameToBlob: any = {};
  const htmlString = replaceAllApartLinks(content);

  ThunkApi.dispatch(uploadingNewItem({ item }));

  return manApi
    .provider_updateQuill(
      item.data.id,
      item.data,
      null,
      htmlString,
      fileNameToBlob,
      store,
      progressCallBack
    )
    .then((res) => {
      ThunkApi.dispatch(
        itemCreated({ localItem: item.data, remoteItem: res.data })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const update_ck = createAsyncThunk('providers/update_ck', update_ckFn);

export const updateFolderFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateFolder(item, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      //return Promise.reject(err);
    });
};
const updateFolder = createAsyncThunk('providers/updateFolder', updateFolderFn);

export const updateSurveyFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateSurvey(item, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      //return Promise.reject(err);
    });
};
const updateSurvey = createAsyncThunk('providers/updateSurvey', updateSurveyFn);

export const updateDocumentFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateDocument(item, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      //return Promise.reject(err);
    });
};
const updateDocument = createAsyncThunk(
  'providers/updateDocument',
  updateDocumentFn
);

export const removeVersionFn = async (
  { item, version }: { item: IProviderItemView; version: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .removeVersion(item, version, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      //return Promise.reject(err);
    });
};
const removeVersion = createAsyncThunk(
  'providers/removeVersion',
  removeVersionFn
);

export const updateQuillFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .provider_updateQuill(null, item, null, null, {}, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateQuill = createAsyncThunk('providers/updateQuill', updateQuillFn);

export const updateLinkFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .updateLink(item, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateLink = createAsyncThunk('providers/updateLink', updateLinkFn);

export const updateExternalLinkFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateExternalLink(item, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateExternalLink = createAsyncThunk(
  'providers/updateExternalLink',
  updateExternalLinkFn
);

export const updateSearchTagFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateSearchTag(item, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateSearchTag = createAsyncThunk(
  'providers/updateSearchTag',
  updateSearchTagFn
);

export const updateSurveyReportFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateReportItem(store, item)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve(res.data);
    })
    .catch((err) => {
      return Promise.reject({ err });
    });
};
const updateSurveyReport = createAsyncThunk(
  'providers/updateSurveyReport',
  updateSurveyReportFn
);

export const updateItemThumbnailFn = async (
  { item, blob }: { item: IProviderItemView; blob: any },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .provider_updateThumbnail(blob, item.id, store)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateItemThumbnail = createAsyncThunk(
  'providers/updateItemThumbnail',
  updateItemThumbnailFn
);

export const createLimitationFn = async (
  { item, type = 'read' }: { item: IProviderItemView; type: LimitationType },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (type === 'read') {
    return manApi
      .createReadLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve({
          limitationId: res.data.readLimitationId!,
        });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  } else if (type === 'edit') {
    return manApi
      .createEditLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve({
          limitationId: res.data.editLimitationId!,
        });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  } else if (type === 'report') {
    return manApi
      .createReportLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve({
          limitationId: res.data.reportLimitationId!,
        });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  } else if (type === 'changeData') {
    return manApi
      .createChangeDataLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve({
          limitationId: res.data.changeDataLimitationId!,
        });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  }

  return Promise.reject();
};
const createLimitation = createAsyncThunk(
  'providers/createLimitation',
  createLimitationFn
);

export const removeLimitationFn = async (
  { item, type = 'read' }: { item: IProviderItemView; type: LimitationType },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (type === 'read') {
    return manApi
      .removeReadLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve();
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  } else if (type === 'edit') {
    return manApi
      .removeEditLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve();
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  } else if (type === 'report') {
    return manApi
      .removeReportLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve();
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  } else if (type === 'changeData') {
    return manApi
      .removeChangeDataLimitation(store, item.id)
      .then((res) => {
        ThunkApi.dispatch(
          itemCreated({ localItem: item, remoteItem: res.data })
        );
        return Promise.resolve();
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  }
  return Promise.reject();
};
const removeLimitation = createAsyncThunk(
  'providers/removeLimitation',
  removeLimitationFn
);

export const item_createNewSurveyFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    createItem({
      item: item,
      apiPromise: (itemCopy: IProviderItemView, state) => {
        itemCopy.id = undefined as any;
        delete (itemCopy as any).surveyId;
        return manApi.provider_addSurvey(itemCopy, state);
      },
    })
  );
};
const item_createNewSurvey = createAsyncThunk(
  'providers/item_createNewSurvey',
  item_createNewSurveyFn
);

export const item_createNewLinkFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    createItem({
      item: item,
      apiPromise: (itemCopy: IProviderItemView, state) => {
        itemCopy.id = undefined as any;
        return manApi.provider_addLink(itemCopy, state);
      },
    })
  );
};
const item_createNewLink = createAsyncThunk(
  'providers/item_createNewLink',
  item_createNewLinkFn
);

export const item_createNewExternalLinkFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    createItem({
      item: item,
      apiPromise: (itemCopy: IProviderItemView, state) => {
        itemCopy.id = undefined as any;
        return manApi.provider_addExternalLink(itemCopy, state);
      },
    })
  );
};
const item_createNewExternalLink = createAsyncThunk(
  'providers/item_createNewExternalLink',
  item_createNewExternalLinkFn
);

export const item_createNewSearchTagFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    createItem({
      item: item,
      apiPromise: (itemCopy: IProviderItemView, state) => {
        itemCopy.id = undefined as any;
        return manApi.provider_addSearchTag(itemCopy, state);
      },
    })
  );
};
const item_createNewSearchTag = createAsyncThunk(
  'providers/item_createNewSearchTag',
  item_createNewSearchTagFn
);

export const createSurveyReportItemFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    createItem({
      item: item,
      apiPromise: (itemCopy: IProviderItemView, state) => {
        itemCopy.id = undefined as any;
        return manApi.addReportItem(state, itemCopy);
      },
    })
  );
};
const createSurveyReportItem = createAsyncThunk(
  'providers/createSurveyReportItem',
  createSurveyReportItemFn
);

export const createNewItemLinkFn = async (
  {
    sourceItemId,
    destinationItemId,
    description,
    tempItemId,
    providerId,
  }: {
    sourceItemId: number;
    destinationItemId: number;
    description: string;
    tempItemId: number | null;
    providerId: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  // if (tempItemId) {
  return manApi
    .addProviderItemLink(sourceItemId, destinationItemId, description, store)
    .then((res) => {
      ThunkApi.dispatch(
        itemLinkCreated({
          sourceItemId: sourceItemId,
          itemLink: res.data,
          tempItemId: tempItemId || null,
          providerId: providerId,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
  // }

  // return Promise.reject();
};
const createNewItemLink = createAsyncThunk(
  'providers/createNewItemLink',
  createNewItemLinkFn
);

export const deleteItemLinkFn = async (
  {
    linkId,
    sourceItemId,
    providerId,
  }: { linkId: number; sourceItemId: number; providerId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .deleteProviderItemLinkById(linkId, store)
    .then(() => {
      ThunkApi.dispatch(
        deleteItemLinkAction({
          linkId: linkId,
          sourceItemId: sourceItemId,
          providerId: providerId,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const deleteItemLink = createAsyncThunk(
  'providers/deleteItemLink',
  deleteItemLinkFn
);

export const restoreItemLinkFn = async (
  { itemId }: { itemId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .restoreProviderItem(itemId, store)
    .then((res) => {
      ThunkApi.dispatch(
        itemCreated({ localItem: res.data, remoteItem: res.data })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const restoreItem = createAsyncThunk(
  'providers/restoreItemLink',
  restoreItemLinkFn
);

export const updateReportFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateReportItem(store, item)
    .then((res) => {
      ThunkApi.dispatch(itemCreated({ localItem: item, remoteItem: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateReport = createAsyncThunk('providers/updateReport', updateReportFn);

export const requireProviderSurveysFn = async (
  { providerId }: { providerId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const providerSurveyStatus =
    store.providers.items[providerId].flag!.providerSurveyStatus;
  if (
    providerSurveyStatus === undefined ||
    providerSurveyStatus === 'notLoaded'
  ) {
    return manApi
      .provider_getMyProviderSurveys(store, providerId)
      .then((res) => {
        ThunkApi.dispatch(
          addAllItem({ items: res.data, providerId: providerId })
        );
        return Promise.resolve();
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
        // return Promise.reject(err);
      });
  }

  return Promise.resolve();
};
const requireProviderSurveys = createAsyncThunk(
  'providers/requireProviderSurveys',
  requireProviderSurveysFn
);

export const requireProviderTagsFn = async (
  { providerId }: { providerId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const isLoading = store.providers.tags?.[providerId]?.status;

  if (isLoading === 'loaded') {
    return Promise.resolve();
  }

  ThunkApi.dispatch(
    setTagsStatus({
      providerId: providerId,
      status: 'loading',
    })
  );
  return manApi
    .tag_getMyTags(providerId, store)
    .then((res) => {
      ThunkApi.dispatch(
        addTags({
          providerId: providerId,
          tags: res.data,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      ThunkApi.dispatch(
        setTagsStatus({
          providerId: providerId,
          status: 'notLoaded',
        })
      );
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const requireProviderTags = createAsyncThunk(
  'providers/requireProviderTags',
  requireProviderTagsFn
);

export const getOrCreateTagFn = async (
  {
    providerId,
    tagList,
    parentId = null,
  }: {
    providerId: number;
    tagList: string[];
    parentId?: number | null;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .tag_getOrCreateTags(store, providerId, tagList, parentId)
    .then((res) => {
      ThunkApi.dispatch(
        addTags({
          providerId: providerId,
          tags: res.data,
        })
      );
      return Promise.resolve({
        providerId: providerId,
        tags: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const getOrCreateTag = createAsyncThunk(
  'providers/getOrCreateTag',
  getOrCreateTagFn
);

export const createATagFn = async (
  { tag, providerId }: { tag: ITagView; providerId: number },
  ThunkApi: TThunkApi
) => {
  const tagList = [tag.text];
  return ThunkApi.dispatch(
    getOrCreateTag({
      providerId,
      tagList,
      parentId: tag.parentId as number | null,
    })
  )
    .unwrap()
    .then(() => {
      ThunkApi.dispatch(removeTag({ providerId: providerId, tagId: tag.id }));
      return Promise.resolve({
        providerId: providerId,
        tagId: tag.id,
      });
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const createATag = createAsyncThunk('providers/createATag', createATagFn);

export const updateExistedTagFn = async (
  { tag, tempTagId }: { tag: ITagView; tempTagId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const providerId = store.thisUser.provider.id;
  return manApi
    .tag_updateTag(store, tag)
    .then((res) => {
      ThunkApi.dispatch(
        updateTagAction({
          providerId: providerId!,
          tag: res.data,
        })
      );
      ThunkApi.dispatch(
        removeTag({
          providerId: providerId!,
          tagId: tempTagId,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateExistedTag = createAsyncThunk(
  'providers/updateExistedTag',
  updateExistedTagFn
);

export const updateTagFn = async (
  { tag, providerId }: { tag: ITagView; providerId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .tag_updateTag(store, tag)
    .then((res) => {
      ThunkApi.dispatch(
        updateTagAction({ providerId: providerId!, tag: res.data })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const updateTag = createAsyncThunk('providers/updateTag', updateTagFn);

export const deleteTagFn = async (
  { tag, providerId }: { tag: ITagView; providerId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .tag_deleteTag(store, tag)
    .then((res) => {
      ThunkApi.dispatch(
        updateTagAction({
          providerId: providerId!,
          tags: res.data as ITagView[],
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const deleteTag = createAsyncThunk('providers/deleteTag', deleteTagFn);

export const restoreTagFn = async (
  { tag, providerId }: { tag: ITagView; providerId: number },

  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .tag_restoreTag(store, tag)
    .then((res) => {
      ThunkApi.dispatch(
        updateTagAction({ providerId: providerId!, tag: res.data })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const restoreTag = createAsyncThunk('providers/restoreTag', restoreTagFn);

export const updateItemFn = async (
  { item }: { item: IProviderItemView },
  ThunkApi: TThunkApi
) => {
  switch (item.type) {
    case providerItemType.document:
      if (item.document.properties.format === documentFormat.quill) {
        return ThunkApi.dispatch(
          updateQuill({
            item: item,
          })
        );
      }
      return ThunkApi.dispatch(
        updateDocument({
          item: item,
        })
      );
    case providerItemType.folder:
      return ThunkApi.dispatch(updateFolder({ item }));
    case providerItemType.survey:
      return ThunkApi.dispatch(updateSurvey({ item }));
    case providerItemType.link:
      return ThunkApi.dispatch(updateLink({ item }));
    case providerItemType.externalLink:
      return ThunkApi.dispatch(updateExternalLink({ item }));
    case providerItemType.searchTag:
      return ThunkApi.dispatch(updateSearchTag({ item }));
    case providerItemType.surveyReport:
      return ThunkApi.dispatch(updateSurveyReport({ item }));
    default:
      return () => {
        return Promise.reject('unsupported file type ' + item.type);
      };
  }
};
const updateItem = createAsyncThunk('providers/updateItem', updateItemFn);

export const searchProviderFn = async (
  {
    queryString,
    variant = null,
    start = 0,
    count = 20,
    filters = [],
    latitude = null,
    longitude = null,
  }: {
    queryString: string;
    variant?: string | null;
    start?: number;
    count?: number;
    filters?: string[];
    latitude?: number | null;
    longitude?: number | null;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .provider_search(
      queryString,
      variant,
      start,
      count,
      latitude,
      longitude,
      filters,
      store
    )
    .then((res) => {
      ThunkApi.dispatch(
        addSeveralProvidersAndManagingCompany({ providers: res.data })
      );
      return Promise.resolve({ providers: res.data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const searchProvider = createAsyncThunk(
  'providers/searchProvider',
  searchProviderFn
);

export const requireDocumentFn = async (
  { documentId, token }: { documentId: number; token?: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .provider_getDocument(documentId, store, token)
    .then((res) => {
      ThunkApi.dispatch(addSingleItem({ item: res.data }));
      return Promise.resolve({
        item: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const requireDocument = createAsyncThunk(
  'providers/requireDocument',
  requireDocumentFn
);

export const requireProviderDocumentsFn = async (
  { documentIds }: { documentIds: number[] },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .getProviderDocuments(documentIds, store)
    .then((res) => {
      if (res.data.length) {
        const providerId = res.data.find(
          (it: any) => it?.providerId
        )?.providerId;
        if (providerId) {
          ThunkApi.dispatch(
            addAllItem({ items: res.data, providerId: providerId })
          );
        }
      }
      return Promise.resolve({
        items: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const requireProviderDocuments = createAsyncThunk(
  'providers/requireProviderDocuments',
  requireProviderDocumentsFn
);

export const requireProviderDocumentsByExtraIdsFn = async (
  { documentIds }: { documentIds: string[] },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .getProviderDocumentsByExtraIds(documentIds, store)
    .then((res) => {
      if (res.data.length) {
        const providerId = res.data.find(
          (it: any) => it?.providerId
        )?.providerId;
        if (providerId) {
          ThunkApi.dispatch(
            addAllItem({ items: res.data, providerId: providerId })
          );
        }
      }
      return Promise.resolve({
        items: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const requireProviderDocumentsByExtraIds = createAsyncThunk(
  'providers/requireProviderDocumentsByExtraIds',
  requireProviderDocumentsByExtraIdsFn
);

export const getFolderViewTypesFn = async (_: any, ThunkApi: TThunkApi) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.provider_getFolderViewTypes(store).then((res) => {
    return Promise.resolve(res);
  });
};
const getFolderViewTypes = createAsyncThunk(
  'providers/getFolderViewTypes',
  getFolderViewTypesFn
);

export const copyItemFn = async (
  {
    srcItemId,
    targetItemId,
    providerId,
    forceCopy,
  }: {
    srcItemId: number;
    targetItemId: number;
    providerId: number;
    forceCopy: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .provider_importContentFromOtherProvider(
      srcItemId,
      targetItemId,
      forceCopy,
      store
    )
    .finally(() => {
      ThunkApi.dispatch(
        requireDocumentInFolder({
          providerId: providerId,
          folderId: targetItemId,
          forceLoad: true,
        })
      );
    });
};
const copyItem = createAsyncThunk('providers/copyItem', copyItemFn);

export const getMyProvidersFn = async (_: any, ThunkApi: TThunkApi) => {
  const store = ThunkApi.getState() as RootState;
  const providers = store.providers;
  if (
    (providers.myProvidersStatus === 'loading' ||
      providers.myProvidersStatus === 'loaded') &&
    Object.keys(providers.map).length > 0
  ) {
    return Promise.resolve(providers.map);
  }

  return manApi
    .getMyProviders(store)
    .then((res) => {
      ThunkApi.dispatch(
        addSeveralProvidersAndManagingCompany({
          providers: res.data,
        })
      );
      return Promise.resolve({
        providers: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const getMyProviders = createAsyncThunk(
  'providers/getMyProviders',
  getMyProvidersFn
);

export const searchDocumentsFn = async (
  {
    searchTerm,
    query,
    providerId,
    searchFields,
    start = 0,
    count = 100,
    folderId,
  }: {
    searchTerm: string;
    query: string;
    providerId: number;
    searchFields: ItemSearchField[];
    start?: number;
    count?: number;
    folderId?: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const searchTermItems = store.providers.items[providerId].search[searchTerm];

  if (searchTermItems) {
    const result = Object.values(store.providers.items[providerId].items)
      .filter((item: IProviderItemViewWithData) =>
        searchTermItems.includes(item.data.id)
      )
      ?.map((item: IProviderItemViewWithData) => item.data);

    return Promise.resolve(result);
  }

  ThunkApi.dispatch(searchInFolderStarted());

  return manApi
    .searchDocuments(
      store,
      query,
      providerId,
      start,
      count,
      searchFields,
      folderId
    )
    .then(({ data: providerItems }) => {
      ThunkApi.dispatch(
        setSearchItems({
          searchTerm: searchTerm,
          providerId: providerId,
          items: providerItems,
        })
      );
      ThunkApi.dispatch(setAppSearchItems());
      return Promise.resolve(providerItems);
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const searchDocuments = createAsyncThunk(
  'providers/searchDocuments',
  searchDocumentsFn
);

export const getAllProvidersFn = async (
  { providersId }: { providersId: number[] },
  ThunkApi: TThunkApi
) => {
  if (providersId.length === 0) {
    return Promise.resolve();
  }
  const store = ThunkApi.getState() as RootState;
  return manApi
    .getAllProviders(store, providersId)
    .then((res) => {
      ThunkApi.dispatch(
        addSeveralProvidersAndManagingCompany({ providers: res.data })
      );
      return Promise.resolve({
        providers: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
      // return Promise.reject(err);
    });
};
const getAllProviders = createAsyncThunk(
  'providers/getAllProviders',
  getAllProvidersFn
);

export const getCompanyManagersFn = async (
  { companyId }: { companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const { data: providers } = await manApi.getByManagerCompany(
    store,
    companyId
  );
  if (providers.length > 0) {
    ThunkApi.dispatch(
      addSeveralProvidersAndManagingCompany({
        providers: providers,
      })
    );
    const relatedProviders = Object.values(providers || {}).reduce(
      (list, provider) => {
        if (provider.rootDocument) {
          list[provider.id] = {
            id: provider.id,
            name: provider.name,
            rootDocumentId: provider.rootDocument.id,
          };
        }
        return list;
      },
      {} as ICompanyProviders
    );
    ThunkApi.dispatch(
      addCompanyProviders({
        companyId: companyId,
        relatedProviders: relatedProviders,
      })
    );
    return Promise.resolve({
      providers,
    });
  }
  return Promise.reject();
};
const getCompanyManagers = createAsyncThunk(
  'providers/getCompanyManagers',
  getCompanyManagersFn
);

export const createItemFn = async (
  {
    item,
    apiPromise,
  }: {
    item: IProviderItemView;
    apiPromise: (item: IProviderItemView, state: RootState) => any;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const reduxItem = store.providers.items[item?.providerId]?.items?.[item.id];

  if (
    !reduxItem ||
    reduxItem?.status === itemStatus.creating ||
    reduxItem?.data?.document?.documentType !== item?.document?.documentType
  ) {
    ThunkApi.dispatch(
      uploadingNewItem({
        item: { data: { ...item } },
      })
    );
    const toUploadItem = JSON.parse(JSON.stringify(item));
    return apiPromise(toUploadItem, store)
      .then((res: { data: any }) => {
        ThunkApi.dispatch(
          itemCreated({
            localItem: item,
            remoteItem: res.data,
          })
        );
        return Promise.resolve({
          remoteItem: res.data,
        });
      })
      .catch((err: any) => {
        ThunkApi.dispatch(
          failToUploadItem({
            item,
            err,
          })
        );
        ThunkApi.rejectWithValue(err) as any;
        return Promise.reject({
          err,
        });
      });
  } else {
    return Promise.reject();
  }
};
const createItem = createAsyncThunk('providers/createItem', createItemFn);

export const pureSearchDocumentsFn = async (
  {
    query,
    providerId,
    start = 0,
    count = 100,
    folderId,
  }: {
    query: string;
    providerId: number;
    start: number;
    count: number;
    folderId?: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .searchDocuments(store, query, providerId, start, count, ['name'], folderId)
    .then(({ data: providerItems }) => {
      ThunkApi.dispatch(
        setSearchItems({
          searchTerm: `custom-search-${Math.random()
            .toString(36)
            .substring(2, 2 + 10)}`,
          providerId: providerId,
          items: providerItems,
        })
      );
      return Promise.resolve({ data: providerItems });
    })
    .catch((err) => Promise.reject(err));
};

const pureSearchDocuments = createAsyncThunk(
  'providers/pureSearchDocuments',
  pureSearchDocumentsFn
);

export const addSeveralProvidersAndManagingCompanyFn = async (
  { providers }: { providers: IProvider[] },
  ThunkApi: TThunkApi
) => {
  ThunkApi.dispatch(
    addSeveralProviders({
      providers,
    })
  );
  providers.forEach((provide) => {
    if (provide.managingCompanyId)
      ThunkApi.dispatch(
        addCompanyProviders({
          companyId: provide.managingCompanyId,
          relatedProviders: {
            [provide.id]: {
              id: provide.id,
              name: provide.name,
              rootDocumentId: provide.rootDocument!.id,
            },
          },
        })
      );
  });
};
const addSeveralProvidersAndManagingCompany = createAsyncThunk(
  'providers/addSeveralProvidersAndManagingCompany',
  addSeveralProvidersAndManagingCompanyFn
);

const providersSlice = createSlice({
  name: 'providers',
  initialState: initialState,
  reducers: {
    survey_addResponse: survey_addResponseHandler,
    loadingProviderData: loadingProviderDataHandler,
    addProviderData: addProviderDataHandler,
    registerProviderData: registerProviderDataHandler,
    updateProviderData: updateProviderDataHandler,
    updateSurveyOwnerPropertiesData: updateSurveyOwnerPropertiesHandler,
    addProviderDataFailed: addProviderDataFailedHandler,
    thisUser_setProviderStatus: thisUser_setProviderStatusHandler,
    addAllItem: addAllItemHandler,
    loadingDocumentsInFolder: loadingDocumentsInFolderHandler,
    failToGetDocumentsInFolder: failToGetDocumentsInFolderHandler,
    creatingNewItem: creatingNewItemHandler,
    createNewItem: createNewItemHandler,
    failToUploadItem: failToUploadItemHandler,
    creatingItemCanceled: creatingItemCanceledHandler,
    uploadingNewItem: uploadingNewItemHandler,
    updateItemProgress: updateItemProgressHandler,
    updateLocalItem: updateLocalItemHandler,
    itemCreated: itemCreatedHandler,
    deleteItem: deleteItemHandler,
    setTagsStatus: setTagsStatusHandler,
    addTags: addTagsHandler,
    createNewTag: createNewTagHandler,
    removeTag: removeTagHandler,
    updateTagAction: updateTagHandler,
    addSingleItem: addSingleItemHandler,
    addSeveralProviders: addSeveralProvidersHandler,
    setMyProvidersLoading: setMyProvidersLoadingHandler,
    setSearchItems: setSearchItemsHandler,
    itemLinkCreated: itemLinkCreatedHandler,
    itemLinkLoading: itemLinkLoadingHandler,
    deleteItemLinkAction: deleteItemLinkHandler,
  },
});

export const {
  survey_addResponse,
  loadingProviderData,
  addProviderData,
  registerProviderData,
  updateProviderData,
  updateSurveyOwnerPropertiesData,
  addProviderDataFailed,
  thisUser_setProviderStatus,
  addAllItem,
  loadingDocumentsInFolder,
  failToGetDocumentsInFolder,
  creatingNewItem,
  createNewItem,
  failToUploadItem,
  creatingItemCanceled,
  uploadingNewItem,
  updateItemProgress,
  updateLocalItem,
  itemCreated,
  deleteItem,
  setTagsStatus,
  addTags,
  createNewTag,
  removeTag,
  updateTagAction,
  addSingleItem,
  addSeveralProviders,
  setMyProvidersLoading,
  setSearchItems,
  itemLinkCreated,
  itemLinkLoading,
  deleteItemLinkAction,
} = providersSlice.actions;

export {
  creatingNewProviderItem,
  createNewProviderItem,
  requireProvider,
  registerProvider,
  importContentFromRemote,
  updateProvider,
  updateSurveyOwnerProperties,
  requireDocumentInFolder,
  requireSearchTag,
  item_createNewFolder,
  item_createNewDocument,
  item_createNewDocumentVersion,
  item_createNewQuill,
  item_delete,
  quill_update,
  update_ck,
  updateFolder,
  updateSurvey,
  updateDocument,
  removeVersion,
  updateQuill,
  updateLink,
  updateExternalLink,
  updateSearchTag,
  updateSurveyReport,
  updateItemThumbnail,
  createLimitation,
  removeLimitation,
  item_createNewSurvey,
  item_createNewLink,
  item_createNewExternalLink,
  item_createNewSearchTag,
  createSurveyReportItem,
  createNewItemLink,
  deleteItemLink,
  restoreItem,
  updateReport,
  requireProviderSurveys,
  requireProviderTags,
  getOrCreateTag,
  createATag,
  updateExistedTag,
  updateTag,
  deleteTag,
  restoreTag,
  updateItem,
  searchProvider,
  requireDocument,
  requireProviderDocuments,
  requireProviderDocumentsByExtraIds,
  getFolderViewTypes,
  copyItem,
  getMyProviders,
  searchDocuments,
  getAllProviders,
  getCompanyManagers,
  createItem,
  addSeveralProvidersAndManagingCompany,
  pureSearchDocuments,
};

export default providersSlice.reducer;
